/**
 * form-submit.js
 * Shared helpers for submitting multipart form requests via fetch.
 */

export function resolveFetch(fetchImpl) {
  if (typeof fetchImpl === 'function') {
    return fetchImpl;
  }
  if (typeof window !== 'undefined' && typeof window.fetch === 'function') {
    return window.fetch.bind(window);
  }
  return null;
}

function normaliseFiles(files) {
  if (!files) {
    return [];
  }
  if (Array.isArray(files)) {
    return files.filter((file) => file != null);
  }
  return [files];
}

function createFormData(fields = {}, files = []) {
  if (typeof FormData !== 'function') {
    throw new Error('FormData implementation is required for multipart requests');
  }

  const formData = new FormData();

  if (fields && typeof fields === 'object') {
    for (const [key, value] of Object.entries(fields)) {
      try {
        formData.append(key, value ?? '');
      } catch (error) {
        formData.append(key, String(value ?? ''));
      }
    }
  }

  for (const file of normaliseFiles(files)) {
    try {
      const name = typeof file?.name === 'string' && file.name ? file.name : undefined;
      if (name !== undefined) {
        formData.append('files', file, name);
      } else {
        formData.append('files', file);
      }
    } catch (error) {
      formData.append('files', file);
    }
  }

  return formData;
}

async function parseErrorResponse(response, contentType) {
  if (!response) {
    return 'network-error';
  }

  try {
    if (contentType && contentType.includes('application/json') && typeof response.json === 'function') {
      const body = await response.json();
      if (body && typeof body === 'object') {
        return body.error || body.message || JSON.stringify(body);
      }
      return JSON.stringify(body);
    }
    if (typeof response.text === 'function') {
      const text = await response.text();
      return text || 'status ' + (response.status ?? 'error');
    }
  } catch (error) {
    return error?.message || 'status ' + (response?.status ?? 'error');
  }

  return 'status ' + (response.status ?? 'error');
}

async function parseSuccessResponse(response, contentType) {
  if (!response) {
    return null;
  }

  if (contentType && contentType.includes('application/json') && typeof response.json === 'function') {
    return response.json();
  }

  if (typeof response.text === 'function') {
    return response.text();
  }

  return null;
}

export async function submitFormRequest({ fetch, url, fields = {}, files = [] } = {}) {
  if (typeof fetch !== 'function') {
    throw new Error('submitFormRequest requires a fetch implementation');
  }
  if (!url) {
    throw new Error('submitFormRequest requires a url');
  }

  const body = createFormData(fields, files);
  const payloadBytes = estimatePayloadBytes(files);
  const started = Date.now();

  // Emit timing for request start
  const emitTiming = typeof window !== 'undefined' && typeof window.emitTiming === 'function'
    ? window.emitTiming
    : null;

  let response;

  try {
    response = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      body,
    });
  } catch (error) {
    const durationMs = Date.now() - started;
    if (emitTiming) {
      emitTiming({ route: url, stage: 'postForm.error', durationMs, payloadBytes });
    }
    const networkError = new Error(error?.message || 'Request failed');
    networkError.cause = error;
    throw networkError;
  }

  const contentType = typeof response?.headers?.get === 'function' ? response.headers.get('content-type') || '' : '';

  if (!response || !response.ok) {
    const durationMs = Date.now() - started;
    if (emitTiming) {
      emitTiming({ route: url, stage: 'postForm.error', durationMs, payloadBytes });
    }
    const status = response?.status ?? 'error';
    let message;
    if (status === 402) {
      message = 'Your organization has reached its monthly AI usage limit. Please contact support.';
    } else if (status === 429) {
      message = 'Too many requests. Please wait a moment before trying again.';
    } else {
      const detail = await parseErrorResponse(response, contentType);
      message = 'Request failed (' + detail + ')';
    }
    const error = new Error(message);
    error.status = status;
    throw error;
  }

  const result = await parseSuccessResponse(response, contentType);
  const durationMs = Date.now() - started;

  // Extract tokens from response if available
  const tokens = result && typeof result === 'object'
    ? (result.tokens || result.usage?.total_tokens || null)
    : null;

  // Emit timing for successful request
  if (emitTiming) {
    emitTiming({ route: url, stage: 'postForm', durationMs, payloadBytes, tokens });
  }

  return result;
}

export function estimatePayloadBytes(files) {
  const list = normaliseFiles(files);
  if (!list.length) {
    return 0;
  }
  return list.reduce((total, file) => total + (typeof file?.size === 'number' ? file.size : 0), 0);
}

if (typeof window !== 'undefined') {
  window.AgileProGateways = window.AgileProGateways || {};
  window.AgileProGateways.__submitFormRequest = submitFormRequest;
}
